import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useGlobalState } from '@/hooks/useGlobalState';
import { maySetupSchool, SCHOOL_SETUP_PATH } from '@/auth/schoolSetupAccess';
import { webRolesOf } from '@/auth/userAccess';
import { useBranches, branchOptionForSchool } from '@/hooks/school';
import { useCurrentSchool } from '@/hooks/useCurrentSchool';

/**
 * Header picker for the school the app works in. Defaults to the
 * organization's first school; the choice lasts for the browser session.
 */
export function SchoolSwitcher() {
  const current = useCurrentSchool();
  const { schools, currentSchool, setChosenSchoolId } = current;
  const branchQuery = useBranches();
  const { access, switchBranch } = useUserAccess();
  const { state } = useGlobalState();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [switching, setSwitching] = useState(false);
  const switchingRef = useRef(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const branches = access ? (branchQuery.data ?? []) : schools.map(branchOptionForSchool);
  const currentBranch = access
    ? branches.find((b) => b.branchId === access.branchId)
    : branches.find((b) => b.school?.schoolId === currentSchool?.schoolId);
  const isLoading = access ? branchQuery.isLoading : current.isLoading;
  const error = access ? branchQuery.error : current.error;

  if (isLoading) {
    return (
      <Box
        role="status"
        aria-label="Loading schools"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1 }}
      >
        <Skeleton variant="circular" width={18} height={18} />
        <Skeleton variant="text" width={70} sx={{ fontSize: '0.875rem' }} />
      </Box>
    );
  }

  if (error || !currentBranch) {
    return (
      <Tooltip title={error ? 'Branches could not be loaded' : 'No authorized branch is available'}>
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          noWrap
          sx={{ px: 1 }}
        >
          {error ? 'Branches unavailable' : 'No branch'}
        </Typography>
      </Tooltip>
    );
  }

  const choose = async (branchId: number) => {
    setAnchor(null);
    if (switchingRef.current || branchId === currentBranch.branchId) return;
    const branch = branches.find((b) => b.branchId === branchId);
    if (!branch) return;
    if (!access) {
      if (branch.school) setChosenSchoolId(branch.school.schoolId);
      return;
    }
    switchingRef.current = true;
    setSwitching(true);
    try {
      const next = await switchBranch(branch);
      const roles = webRolesOf(next, next.menus);
      const activeRole = roles.find((r) => r.code === state.activeRole) ?? roles[0];
      const home =
        !branch.school && maySetupSchool(next, next.menus, activeRole?.code, branch)
          ? SCHOOL_SETUP_PATH
          : activeRole?.home;
      if (home) void navigate(home, { replace: true });
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'The branch could not be switched. Try again.',
        { variant: 'error' },
      );
    } finally {
      switchingRef.current = false;
      setSwitching(false);
    }
  };

  return (
    <>
      <Tooltip title="Switch branch">
        <Box
          role="button"
          tabIndex={0}
          aria-label={`School: ${currentBranch.school?.schoolName ?? currentBranch.branchName}`}
          aria-haspopup="menu"
          aria-expanded={anchor ? 'true' : undefined}
          aria-disabled={switching}
          aria-busy={switching}
          onClick={(e) => {
            if (!switching) setAnchor(e.currentTarget);
          }}
          onKeyDown={(e) => {
            if (!switching && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setAnchor(e.currentTarget);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            maxWidth: { xs: 140, md: 240 },
            borderRadius: 2,
            cursor: 'pointer',
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <SchoolOutlinedIcon fontSize="small" />
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: 'text.primary' }}>
            {currentBranch.school?.aliasName ||
              (currentBranch.school?.schoolName ?? currentBranch.branchName)}
          </Typography>
          <ExpandMoreOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 260, maxHeight: 400 } } }}
      >
        {branches.map((branch) => {
          const school = branch.school;
          const active = branch.branchId === currentBranch.branchId;
          return (
            <MenuItem
              key={branch.branchId}
              selected={active}
              disabled={switching}
              onClick={() => void choose(branch.branchId)}
            >
              <ListItemText
                primary={school?.schoolName ?? branch.branchName}
                secondary={
                  school
                    ? [school.schoolCode, school.city].filter(Boolean).join(' · ')
                    : 'School setup required'
                }
              />
              {active && <CheckRoundedIcon fontSize="small" color="primary" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
